import { useEffect, useState } from "react";
import { useModals } from "../common/ModalsProvider.js";
import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";
import { abrirAbaPendente } from "../../lib/open-file.js";
import { trpc } from "../../lib/trpc.js";

/**
 * F-releve-conta (CEO 2026-11-03) — janela do relevé de compte do cliente:
 * "Em aberto hoje" (reste dû por fatura + faixas de atraso) ou "Extrato por
 * período" (saldo anterior, movimentos, saldo final). Botões: abrir o PDF e
 * enviar por e-mail aos contatos do cliente. Tudo em TTC.
 */
export function CustomerStatementModal({
  companyId,
  customerId,
  onClose,
}: {
  companyId: number;
  customerId: number;
  onClose: () => void;
}) {
  const t = useT();
  const modals = useModals();
  const hoje = new Date().toLocaleDateString("sv-SE");
  const inicioMes = hoje.slice(0, 8) + "01";
  const [modo, setModo] = useState<"aberto" | "periodo">("aberto");
  const [de, setDe] = useState(inicioMes);
  const [ate, setAte] = useState(hoje);
  const [email, setEmail] = useState("");
  const [emailEditado, setEmailEditado] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [mensagemEditada, setMensagemEditada] = useState(false);
  const periodoOk = modo === "aberto" || (!!de && !!ate && de <= ate);
  const entrada = { companyId, customerId, modo, ...(modo === "periodo" ? { de, ate } : {}) };
  const preview = trpc.customerStatements.preview.useQuery(entrada, { enabled: periodoOk });
  const p = preview.data;

  // Esc fecha (como as outras janelas do Gestion).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    if (!p) return;
    if (!emailEditado) {
      const padrao = p.destinatarios.find((d) => d.receivesInvoices) ?? p.destinatarios[0];
      if (padrao) setEmail(padrao.email);
    }
    if (!mensagemEditada) setMensagem(p.textoPadrao);
  }, [p, emailEditado, mensagemEditada]);

  const pdfMut = trpc.customerStatements.pdf.useMutation();
  const sendMut = trpc.customerStatements.send.useMutation({
    onSuccess: () => {
      void modals.alert({
        title: t("customerDetail.stmtSentTitle"),
        message: `${t("customerDetail.stmtSentMsg")} ${email.trim()}.`,
      });
      onClose();
    },
    onError: (e) =>
      void modals.alert({
        title: t("customerDetail.stmtErrorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const abrirPdf = () => {
    const aba = abrirAbaPendente();
    pdfMut.mutate(entrada, {
      onSuccess: (r) => aba.ir(r.url),
      onError: (e) => {
        aba.fechar();
        void modals.alert({
          title: t("customerDetail.stmtErrorTitle"),
          message: e.message,
          tone: "danger",
        });
      },
    });
  };
  const money = (v: number) => formatMoney(v, { decimals: 2 });
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const campo =
    "mt-1 w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm outline-none focus:border-lime";
  const chip = (ativo: boolean) =>
    `rounded-full border px-3 py-1 text-xs ${ativo ? "border-lime bg-lime/15 text-lime" : "border-neutral-700 hover:border-lime"}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-lg space-y-3 rounded-xl border border-neutral-700 bg-neutral-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="statement-modal"
      >
        <h2 className="text-base font-semibold">
          {t("customerDetail.stmtTitle")}
          {p?.cliente ? ` — ${p.cliente}` : ""}
        </h2>
        <div className="flex flex-wrap gap-2" data-testid="statement-mode">
          <button
            type="button"
            onClick={() => setModo("aberto")}
            className={chip(modo === "aberto")}
            data-testid="statement-mode-open"
          >
            {t("customerDetail.stmtModeOpen")}
          </button>
          <button
            type="button"
            onClick={() => setModo("periodo")}
            className={chip(modo === "periodo")}
            data-testid="statement-mode-period"
          >
            {t("customerDetail.stmtModePeriod")}
          </button>
        </div>
        {modo === "periodo" && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-neutral-400">
              {t("customerDetail.stmtFrom")}
              <input
                type="date"
                value={de}
                max={ate}
                onChange={(e) => setDe(e.target.value)}
                className={campo}
                data-testid="statement-from"
              />
            </label>
            <label className="block text-xs text-neutral-400">
              {t("customerDetail.stmtTo")}
              <input
                type="date"
                value={ate}
                min={de}
                max={hoje}
                onChange={(e) => setAte(e.target.value)}
                className={campo}
                data-testid="statement-to"
              />
            </label>
          </div>
        )}
        <div
          className="rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm"
          data-testid="statement-preview"
        >
          {!p && (
            <span className="text-neutral-500">{preview.error ? preview.error.message : "…"}</span>
          )}
          {p && modo === "periodo" && (
            <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
              <dt className="text-neutral-400">{t("customerDetail.stmtOpening")}</dt>
              <dd className="text-right font-mono">{money(p.saldoInicial)}</dd>
              <dt className="text-neutral-400">{t("customerDetail.stmtMovements")}</dt>
              <dd className="text-right font-mono">{p.movimentos}</dd>
              <dt className="font-medium">{t("customerDetail.stmtClosing")}</dt>
              <dd className="text-right font-mono font-semibold" data-testid="statement-closing">
                {money(p.saldoFinal)}
              </dd>
            </dl>
          )}
          {p && modo === "aberto" && (
            <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
              <dt className="text-neutral-400">{t("customerDetail.stmtOpenInvoices")}</dt>
              <dd className="text-right font-mono">{p.abertas}</dd>
              <dt className="text-neutral-400">{t("customerDetail.stmtTotalOpen")}</dt>
              <dd className="text-right font-mono" data-testid="statement-total-open">
                {money(p.totalAberto)}
              </dd>
              {p.vencido > 0 && (
                <>
                  <dt className="text-red-400">{t("customerDetail.stmtOverdue")}</dt>
                  <dd className="text-right font-mono text-red-400">{money(p.vencido)}</dd>
                </>
              )}
              {p.creditoDisponivel > 0 && (
                <>
                  <dt className="text-neutral-400">{t("customerDetail.stmtCredit")}</dt>
                  <dd className="text-right font-mono">− {money(p.creditoDisponivel)}</dd>
                </>
              )}
              <dt className="font-medium">{t("customerDetail.stmtToPay")}</dt>
              <dd className="text-right font-mono font-semibold" data-testid="statement-to-pay">
                {money(p.saldoAPagar)}
              </dd>
            </dl>
          )}
        </div>
        <label className="block text-sm text-neutral-300">
          {t("customerDetail.stmtRecipient")}
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailEditado(true);
            }}
            className={campo}
            data-testid="statement-email"
          />
        </label>
        {p && p.destinatarios.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {p.destinatarios.map((d) => (
              <button
                key={d.email}
                type="button"
                onClick={() => {
                  setEmail(d.email);
                  setEmailEditado(true);
                }}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] ${email.trim().toLowerCase() === d.email.toLowerCase() ? "border-lime bg-lime/15" : "border-neutral-700 hover:border-lime"}`}
                title={d.email}
              >
                {d.receivesInvoices ? "✉ " : ""}
                {d.name}
              </button>
            ))}
          </div>
        )}
        {p && p.destinatarios.length === 0 && (
          <p className="text-xs text-amber-400">{t("customerDetail.stmtNoEmail")}</p>
        )}
        <label className="block text-sm text-neutral-300">
          {t("customerDetail.stmtMessage")}
          <textarea
            value={mensagem}
            onChange={(e) => {
              setMensagem(e.target.value);
              setMensagemEditada(true);
            }}
            rows={4}
            maxLength={4000}
            className={campo + " resize-y"}
            data-testid="statement-message"
          />
        </label>
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:bg-neutral-800"
          >
            {t("customerDetail.stmtClose")}
          </button>
          <button
            type="button"
            onClick={abrirPdf}
            disabled={!p || pdfMut.isPending}
            className="rounded-md border border-lime/50 px-3 py-1.5 text-sm text-lime hover:bg-lime/10 disabled:opacity-40"
            data-testid="statement-pdf"
          >
            {t("customerDetail.stmtPdfBtn")}
          </button>
          <button
            type="button"
            onClick={() =>
              sendMut.mutate({
                ...entrada,
                para: email.trim(),
                mensagem: mensagem.trim() || undefined,
              })
            }
            disabled={!p || !emailOk || sendMut.isPending}
            className="rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime hover:opacity-90 disabled:opacity-40"
            data-testid="statement-send"
          >
            {sendMut.isPending ? "…" : t("customerDetail.stmtSendBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
